import React from 'react';
import { useLocation } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import moment from 'moment';
import configuration from './configuration';
import {
  SUMMARY_PRINT_REPORT_VALUE,
  DETAILED_PRINT_REPORT_VALUE,
} from './reducers/report';
import restApi from './restApi';
import { useSelector } from 'react-redux';
import { AddressMap } from './AddressMap';
import {
  preparePrintDataDetailed,
  preparePrintDataSummary,
} from './reducers/report';
import RouteSheet from './components/RouteSheet';
import { Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import StickyButtonContainer from './components/StickyButtonContainer.js';
const AUTOPRINT_MODE = 'autoprint';

const parseQueryParams = search => {
  const queryParams = new URLSearchParams(search);
  const params = {};
  const from = queryParams.get('from');
  if (!from) {
    params.from = new Date(Date.now() - 24 * 60 * 60 * 1000); // default from is 24h ago
  } else {
    params.from = new Date(from);
  }
  const to = queryParams.get('to');
  if (!to) {
    params.to = new Date(); // default to is now
  } else {
    params.to = new Date(to);
  }
  const type = queryParams.get('type');
  if (
    type &&
    [SUMMARY_PRINT_REPORT_VALUE, DETAILED_PRINT_REPORT_VALUE].includes(
      type.toLowerCase(),
    )
  ) {
    params.type = type;
  } else {
    params.type = DETAILED_PRINT_REPORT_VALUE; // default type is detailed
  }
  const objectId = queryParams.get('objectId');
  if (objectId) {
    params.objectId = objectId;
  } else {
    throw new Error('objectId query parameter is required');
  }
  const mode = queryParams.get('mode');
  if (mode === AUTOPRINT_MODE) {
    params.mode = AUTOPRINT_MODE;
  }
  return params;
};

const preparePrintData = (object, queryParams, options) => {
  if (queryParams.type === DETAILED_PRINT_REPORT_VALUE) {
    return preparePrintDataDetailed(object, queryParams, { options });
  } else {
    return preparePrintDataSummary(object, queryParams, { options });
  }
};

const ReportRouteSheet = () => {
  const { addToast } = useToasts();
  const login = useSelector(state => state.login);
  const location = useLocation();
  const [addressMap, setAddressMap] = React.useState();
  const [pointCoords, setPointCoords] = React.useState();
  const [rawData, setRawData] = React.useState();
  const [printData, setPrintData] = React.useState();
  const [trackingId, setTrackingId] = React.useState();
  const [statusMessage, setStatusMessage] = React.useState();
  const [hasError, setHasError] = React.useState(false);
  const reportOptions = useSelector(state => state.report.options);
  const [loaded, setLoaded] = React.useState(false);
  const classes = useStyles();
  const [disableButton, setDisableButton] = React.useState(true);

  // Initialize URLSearchParams with the current 'search' string
  const { from, to, type, objectId, mode } = React.useMemo(() => {
    return parseQueryParams(location.search);
  }, [location.search]);
  React.useEffect(() => {
    restApi.setAuthTokens(login.jwt);
  }, [login.jwt]);

  React.useEffect(() => {
    window.__fleet_manager__ = window.__fleet_manager__ || {};
    configuration();
    setRawData(null);
    setPrintData(null);
    setTrackingId(null);
    setPointCoords(null);
    setAddressMap(null);
    setLoaded(false);
    setStatusMessage('Loading...');
    setHasError(false);

    const start = moment(from);
    const end = moment(to);
    restApi
      .getReportViewAsync('print', objectId, start, end)
      .then(json => {
        if (json.ok) {
          if (json.data.periods.length === 0) {
            setLoaded(true);
            addToast('Няма данни за избрания период.', {
              appearance: 'info',
            });
            setStatusMessage('Няма данни за избрания период.');
            return;
          } else {
            setRawData(json.data);
          }
        } else if (json.code === 404) {
          // TODO: No data for the specified period
        }
      })
      .catch(ex => {
        addToast('Възникна грешка при опит да се направи справка', {
          appearance: 'error',
          onDismiss: () => {
            // TODO: Maybe close the current view and return to the previous one
          },
        });
        setHasError(true);
        setStatusMessage('Възникна грешка при зареждане на данните.');
        console.error(ex);
      });
  }, [location.search]);

  React.useEffect(() => {
    if (rawData) {
      const data = preparePrintData(
        rawData,
        {
          start: moment(from),
          end: moment(to),
          name: rawData.name,
          plateNumber: rawData.plateNumber,
          type: type,
        },
        reportOptions,
      );
      setPrintData(data);
    }
  }, [rawData]);

  React.useEffect(() => {
    if (printData) {
      const coords = printData.map.geometry.coordinates;
      restApi.startReverseGeocodingAsync(coords).then(resp => {
        setTrackingId(resp.data.trackingId);
        setPointCoords(coords);
      });
    }
  }, [printData]);

  React.useEffect(() => {
    if (trackingId && pointCoords) {
      restApi.getReverseGeocodingAsync(trackingId).then(resp => {
        if (resp.ok) {
          const map = new AddressMap(
            resp.data.addressList.map((item, index) => [
              pointCoords[index],
              item,
            ]),
          );
          setAddressMap(map);
        } else {
          addToast('Адресите не могат да се изчислят.', {
            appearance: 'error',
          });
        }
      });
    }
  }, [addToast, pointCoords, trackingId]);

  const header = React.useMemo(() => {
    if (printData) {
      const { startDist, endDist, totalRest, totalWork, totalDist } =
        printData.print || {};

      const dayWorkStart = Math.min.apply(
        null,
        printData.print.days.map(day => day.dayWorkStart).filter(item => item),
      );
      const dayWorkEnd = Math.max.apply(
        null,
        printData.print.days.map(day => day.dayWorkEnd).filter(item => item),
      );
      return {
        objectName: printData.objectName,
        plateNumber: printData.plateNumber,
        startTime: moment(dayWorkStart).format('DD.MM.YYYY HH:mm'),
        endTime: moment(dayWorkEnd).format('DD.MM.YYYY HH:mm'),
        mileageStart: startDist,
        mileageEnd: endDist,
        totalRestHours: totalRest,
        totalWorkHours: totalWork,
        totalDistance: totalDist,
      };
    }
  }, [from, printData, to]);
  React.useEffect(() => {
    if (printData && header && addressMap) {
      setLoaded(true);
    }
  }, [header, printData, addressMap]);

  React.useEffect(() => {
    // 1. Guard clause: only run if loaded and in the correct mode
    if (!loaded) {
      return;
    }

    // 2. Notify external manager
    if (window.__fleet_manager__?.onLoaded) {
      window.__fleet_manager__.onLoaded({ hasError, statusMessage });
    }

    setDisableButton(false); // Enable the print button once everything is loaded
  }, [hasError, header, loaded, mode, statusMessage]);

  const handlePrintClick = () => {
    // 1. Set up Print Logic
    const originalTitle = document.title;
    const newTitle = `${header.plateNumber} - ${header.startTime} - ${header.endTime}`;
    document.title = newTitle;

    const handleAfterPrint = () => {
      window.onafterprint = null; // Clean up listener
      document.title = originalTitle;
      // window.close();
    };

    window.onafterprint = handleAfterPrint;
    window.print();

    return () => {
      window.onafterprint = null;
    };
  };

  if (printData && header) {
    return (
      <StickyButtonContainer
        text='Принтирай'
        onClick={handlePrintClick}
        disableButton={disableButton}
      >
        <Box className={classes.container}>
          <Box className={classes.sheet}>
            <RouteSheet
              data={printData.print}
              header={header}
              reportType={type}
              addressMap={addressMap}
            />
          </Box>
        </Box>
      </StickyButtonContainer>
    );
  } else {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='100vh'
      >
        <Typography variant='h4'>{statusMessage}</Typography>
      </Box>
    );
  }
};

const useStyles = makeStyles({
  '@global': {
    body: {
      overflow: 'hidden',
    },
    '@media print': {
      body: {
        overflow: 'auto',
      },
    },
  },
  printButton: {
    opacity: 0.3,
    position: 'absolute',
    left: 10,
    bottom: 10,
    zIndex: 1000,
    '&:hover': {
      opacity: 1,
    },
  },
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  sheet: {
    width: '100%',
    height: '100%',
    overflow: 'scroll',
  },
  hideOnPrint: {
    display: 'block',
    '@media print': {
      display: 'none',
    },
  },
});

export default ReportRouteSheet;
